import React, {
  useCallback, useContext, useEffect, useMemo,
} from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tooltip from '@amzn/meridian/tooltip';
import Column from '@amzn/meridian/column';
import {
  Locale,
  LocaleUserFriendlyString,
} from '@amzn/coral_com-amazon-passportusermanagementservice';
import { useLocation, matchPath } from 'react-router';
import useUserProfile from '@amzn/PassportUserManagementUIAssets/dist/hooks/useUserProfile';
import useUpdateUserLocale from '@amzn/PassportUserManagementUIAssets/dist/hooks/useUpdateUserLocale';
import CarrierLogisticsCommonsContext from '@amzn/carrierlogistics-commons/dist/context/CarrierLogisticsCommonsContext';
import CarrierLogisticsCommonsDispatchContext from '@amzn/carrierlogistics-commons/dist/context/CarrierLogisticsCommonsDispatchContext';
import {
  setLocaleForAuthPortal,
  setLocaleToLocalStorage,
} from '../utils/localeUtils';
import { DEFAULT_LOCALE } from '../config/i18n';
import routeConfig from '../routeConfig';
import { LANGUAGE_TOGGLE_UNSUPPORTED } from '../constants/localisation';
import { recordCustomEventInCloudWatchRUM } from '@amzn/carrierlogistics-commons/dist/utils/cloudwatchRum';
import { DROPDOWN_ITEM_CLICK } from '@amzn/carrierlogistics-commons/dist/constants/analytics';
import { LOCALE_DROPDOWN_ITEM_CLICK } from '../constants/analytics';
export const LanguageToggle = () => {
  const { pathname } = useLocation();
  const { defaultLocale = DEFAULT_LOCALE } = useUserProfile();
  const {
    localization: { locale = DEFAULT_LOCALE },
  } = useContext(CarrierLogisticsCommonsContext);
  const { localisationDispatch } = useContext(
    CarrierLogisticsCommonsDispatchContext,
  );

  const { updateUserLocale } = useUpdateUserLocale();

  const toggleLocale = useCallback(
    (value) => {
      localisationDispatch?.dispatchStoreLocale(value);
      setLocaleForAuthPortal(value.replace('-', '_'));
      setLocaleToLocalStorage(value.replace('-', '_'));

      if (value) {
        recordCustomEventInCloudWatchRUM(DROPDOWN_ITEM_CLICK, {
          action_properties: {
            name: LOCALE_DROPDOWN_ITEM_CLICK,
          },
          extra_properties: {
            locale: value,
          },
        });
        updateUserLocale({ defaultLocale: value.replace('-', '_') });
      }
    },

    [localisationDispatch, updateUserLocale],
  );

  useEffect(() => {
    localisationDispatch?.dispatchStoreLocale(
      defaultLocale === Locale.EN
        ? DEFAULT_LOCALE
        : defaultLocale.replace('_', '-'),
    );
  }, [defaultLocale]);

  const getLocaleOptions = () => Object.keys(Locale)
    .slice(1)
    .map((option) => (
      <SelectOption
        label={LocaleUserFriendlyString[option]}
        value={Locale[option].replace('_', '-')}
        key={Locale[option].replace('_', '-')}
      />
    ));

  const isLanguageToggleDisabled = useMemo(() => {
    const requiredRouteConfig = routeConfig.find(rc => {
      const match = matchPath(pathname, {
        path: rc.path,
      });
      return match?.path === rc.path || pathname.includes(rc.path);
    });
    if (
      (requiredRouteConfig && requiredRouteConfig.isLanguageToggleSupported) ||
      pathname === '/'
    ) {
      return false;
    }

    return true;
  }, [pathname]);

  const isLanguageToggleHidden = useMemo(() => {
    const requiredRouteConfig = routeConfig.find((rc) => {
      const match = matchPath(pathname, {
        path: rc.path,
      });
      return match?.path === rc.path || pathname.includes(rc.path);
    });

    return !!requiredRouteConfig?.isLanguageToggleHidden;
  }, [pathname]);

  return (
    !isLanguageToggleHidden
    && (isLanguageToggleDisabled ? (
      <Tooltip title={LANGUAGE_TOGGLE_UNSUPPORTED} position="top">
        <Column>
          <Select
            id="language-toggle"
            value={DEFAULT_LOCALE}
            size="small"
            disabled
          >
            {getLocaleOptions()}
          </Select>
        </Column>
      </Tooltip>
    ) : (
      <Select
        id="language-toggle"
        value={locale}
        onChange={toggleLocale}
        size="small"
      >
        {getLocaleOptions()}
      </Select>
    ))
  );
};

export default LanguageToggle;
